import React, { useCallback, useState } from 'react';

import { CANNED_FIELD_METADATA, getIsAutoOnlyCannedVariant, isValidEmail } from '@aline/common';
import { IconButton, InputLabel, Typography } from '@mui/material';
import { SquarePen } from 'lucide-react';
import { AlineInput } from 'src/AlineInput';
import {
  AlineTooltip,
  FieldRecipientAutoComplete,
  workflowStepsDialogOpenVar,
} from 'src/components';
import { CANNED_ICON_BY_VARIANT } from 'src/components/icons/CannedFieldIcons';
import { DateFormatSelect } from 'src/components/variables/document-variables/DateFormatSelect';
import { useCannedFieldValueWrite } from 'src/components/variables/document-variables/use-canned-field-value-write';
import { useCurrentUserId } from 'src/gql';
import { Apollo } from 'src/gql/generated';
import { useDocumentParams } from 'src/helpers';
import { useDocumentSignatureStatus } from 'src/hooks';
import { makeStyles } from 'tss-react/mui';

import {
  getCanEditCannedFieldForm,
  getCannedFieldFormDisabledReason,
  getCannedFieldFormDisplayValue,
  getCannedFieldTypeDisplayString,
  getCanToggleCannedFieldEdit,
  useCancelCannedWriteOnUnlock,
} from './canned-field-form-state';

const useStyles = makeStyles()(theme => ({
  editIcon: {
    height: 16,
    width: 16,
  },
  editIconActive: {
    color: theme.palette.primary.main,
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    padding: theme.spacing(1, 2),
  },
  inputLabel: {
    ...theme.appTypography.fieldLabel,
    marginBottom: theme.spacing(0.25),
  },
  readOnlyBox: {
    alignItems: 'center',
    border: `1px solid ${theme.appColors.rule}`,
    borderRadius: 3,
    display: 'flex',
    gap: theme.spacing(1),
    height: theme.appSizes.inputHeight,
    padding: theme.spacing(0, 1),
  },
  readOnlyIcon: {
    height: 18,
    width: 18,
  },
  readOnlyText: {
    fontSize: 14,
  },
  subtitle: {
    color: theme.appColors.secondaryType,
    display: 'block',
    lineHeight: 1.3,
    marginBottom: theme.spacing(1),
  },
  valueLabel: {
    ...theme.appTypography.fieldLabel,
    marginBottom: 0,
  },
  valueLabelRow: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(0.25),
  },
}));

export type CannedFieldFormField = Pick<
  Apollo.CannedFieldFullFragment,
  | '__typename'
  | 'assignee'
  | 'dateFormat'
  | 'defaultValue'
  | 'id'
  | 'value'
  | 'variant'
  | 'workflowId'
>;

interface CannedFieldFormProps {
  cannedField: CannedFieldFormField;
  formDataCy?: string;
}

export function CannedFieldForm({ cannedField, formDataCy }: CannedFieldFormProps): JSX.Element {
  const { classes, cx } = useStyles();
  const currentUserId = useCurrentUserId();
  const { documentId } = useDocumentParams();
  const { wasSentForSignature } = useDocumentSignatureStatus(documentId);
  const meta = CANNED_FIELD_METADATA[cannedField.variant];
  const Icon = CANNED_ICON_BY_VARIANT[cannedField.variant];
  const [isEditingValue, setIsEditingValue] = useState(false);
  const isAutoOnly = getIsAutoOnlyCannedVariant(cannedField.variant);
  const isAssignedToCurrentUser =
    cannedField.assignee?.assignee?.id != null &&
    cannedField.assignee.assignee.id === currentUserId;
  const effectiveValue = cannedField.value ?? cannedField.defaultValue ?? '';
  const hasValue = effectiveValue !== '';
  const canToggleEdit = getCanToggleCannedFieldEdit({
    assigneeStatus: cannedField.assignee?.status,
    hasValue,
    isAssignedToCurrentUser,
    isAutoOnly,
    wasSentForSignature,
  });

  return (
    <div className={classes.form} data-cy={formDataCy}>
      <div>
        <InputLabel className={classes.inputLabel}>Assignee</InputLabel>
        <CannedFieldAssignment cannedField={cannedField} disabled={wasSentForSignature} />
      </div>
      <div>
        <InputLabel className={classes.inputLabel}>Type</InputLabel>
        <div className={classes.readOnlyBox}>
          <Icon className={classes.readOnlyIcon} />
          <Typography noWrap className={classes.readOnlyText}>
            {getCannedFieldTypeDisplayString(cannedField.variant)}
          </Typography>
        </div>
      </div>
      <div>
        <InputLabel className={classes.inputLabel}>Name</InputLabel>
        <div className={classes.readOnlyBox}>
          <Typography noWrap className={classes.readOnlyText}>
            {meta.label}
          </Typography>
        </div>
      </div>
      {cannedField.variant === Apollo.CannedFieldVariant.DateSigned && (
        <CannedFieldDateFormat cannedField={cannedField} disabled={wasSentForSignature} />
      )}
      <div>
        <div className={classes.valueLabelRow}>
          <InputLabel className={classes.valueLabel}>Value</InputLabel>
          {canToggleEdit && (
            <AlineTooltip
              arrow
              placement="top"
              title={isEditingValue ? 'Stop editing' : 'Edit value'}
            >
              <IconButton size="small" onClick={() => setIsEditingValue(prev => !prev)}>
                <SquarePen
                  className={cx(classes.editIcon, isEditingValue && classes.editIconActive)}
                />
              </IconButton>
            </AlineTooltip>
          )}
        </div>
        <Typography className={classes.subtitle} variant="caption">
          Auto-populated field
        </Typography>
        <CannedFieldValueInput
          cannedField={cannedField}
          isEditingLocked={canToggleEdit && !isEditingValue}
        />
      </div>
    </div>
  );
}

interface CannedFieldAssignmentProps {
  cannedField: CannedFieldFormField;
  disabled: boolean;
}

function CannedFieldAssignment({ cannedField, disabled }: CannedFieldAssignmentProps): JSX.Element {
  const [updateCannedField] = Apollo.useUpdateCannedFieldMutation();

  return (
    <FieldRecipientAutoComplete
      autoSelectFirstRecipient={false}
      disabled={disabled}
      value={cannedField.assignee ?? null}
      onChange={async option => {
        if (!option) return;
        if (option.__typename === 'NewFieldRecipient') {
          workflowStepsDialogOpenVar(true);
          return;
        }
        await updateCannedField({
          variables: { input: { assigneeId: option.id, id: cannedField.id } },
        });
      }}
    />
  );
}

interface CannedFieldDateFormatProps {
  cannedField: CannedFieldFormField;
  disabled: boolean;
}

function CannedFieldDateFormat({ cannedField, disabled }: CannedFieldDateFormatProps): JSX.Element {
  const { classes } = useStyles();
  const [updateCannedField] = Apollo.useUpdateCannedFieldMutation();
  const changeDateFormat = useCallback(
    async (dateFormat: Apollo.DateFieldFormat) => {
      await updateCannedField({ variables: { input: { dateFormat, id: cannedField.id } } });
    },
    [cannedField.id, updateCannedField]
  );

  return (
    <div>
      <InputLabel className={classes.inputLabel}>Date format</InputLabel>
      <DateFormatSelect
        disabled={disabled}
        value={cannedField.dateFormat}
        onChange={changeDateFormat}
      />
    </div>
  );
}

interface CannedFieldValueInputProps {
  cannedField: CannedFieldFormField;
  isEditingLocked: boolean;
}

function CannedFieldValueInput({
  cannedField,
  isEditingLocked,
}: CannedFieldValueInputProps): JSX.Element {
  const currentUserId = useCurrentUserId();
  const { documentId } = useDocumentParams();
  const [isFocused, setIsFocused] = useState(false);
  const isAutoOnly = getIsAutoOnlyCannedVariant(cannedField.variant);
  const [value, setValue] = useState<string>(cannedField.value ?? '');
  const { wasSentForSignature } = useDocumentSignatureStatus(documentId);
  const [committedValue, setCommittedValue] = useState<string | null>(cannedField.value);
  const isEmail = cannedField.variant === Apollo.CannedFieldVariant.UserEmail;
  const isAssignedToCurrentUser =
    cannedField.assignee?.assignee?.id != null &&
    cannedField.assignee.assignee.id === currentUserId;
  const canEdit = getCanEditCannedFieldForm({
    assigneeStatus: cannedField.assignee?.status,
    isAssignedToCurrentUser,
    isAutoOnly,
    isEditingLocked,
    wasSentForSignature,
  });
  const disabledReason = getCannedFieldFormDisabledReason({
    assigneeStatus: cannedField.assignee?.status,
    isAssignedToCurrentUser,
    isAutoOnly,
    isEditingLocked,
    variant: cannedField.variant,
    wasSentForSignature,
  });

  if (cannedField.value !== committedValue) {
    setCommittedValue(cannedField.value);
    if (!isFocused) setValue(cannedField.value ?? '');
  }

  const { cancelWrite, flushWrite, writeValue } = useCannedFieldValueWrite({
    cannedFieldId: cannedField.id,
    isEmail,
    savedValue: cannedField.value,
    setValue,
    workflowId: cannedField.workflowId,
  });
  const emailError = isEmail && canEdit && value !== '' && !isValidEmail(value);
  const displayValue = getCannedFieldFormDisplayValue({
    canEdit,
    defaultValue: cannedField.defaultValue,
    isAutoOnly,
    savedValue: cannedField.value,
    typedValue: value,
  });

  useCancelCannedWriteOnUnlock({ isEditingLocked, onCancel: cancelWrite });

  return (
    <AlineTooltip arrow placement="top" title={disabledReason}>
      <div>
        <AlineInput
          hideHelperText
          disabled={!canEdit}
          error={emailError}
          helperText={emailError ? 'Please enter a valid email address.' : undefined}
          placeholder={canEdit ? '' : (cannedField.defaultValue ?? '')}
          type={isEmail ? 'email' : 'text'}
          value={displayValue}
          onBlur={() => {
            setIsFocused(false);
            flushWrite();
          }}
          onChange={e => writeValue(e.target.value)}
          onFocus={() => setIsFocused(true)}
        />
      </div>
    </AlineTooltip>
  );
}
